import React, { useState } from 'react';
import { useController } from 'react-hook-form';

import { Flex } from '@rover/kibble/core';
import { Form, useKibbleForm } from '@rover/kibble/forms';

import { SkillsAndBehaviorSlugsDictionary } from '../../../utils/skillAndBehaviorQueryParams';

import { DesktopTrainingNeedsInput } from './DesktopTrainingNeedsInput';
import { MobileTrainingNeedsInput } from './MobileTrainingNeedsInput';
import { Schema, schema } from './needsInputSchema';

type TrainingNeedsFormProps = {
  selectedSkillsAndBehaviors: SkillsAndBehaviorSlugsDictionary;
  updateSkillsAndBehaviors: (skillsAndBehaviors: SkillsAndBehaviorSlugsDictionary) => void;
};

export const TrainingNeedsForm = ({
  selectedSkillsAndBehaviors,
  updateSkillsAndBehaviors,
}: TrainingNeedsFormProps): JSX.Element | null => {
  const [showBottomSheet, setShowBottomSheet] = useState<boolean>(false);

  const handleBottomSheetSubmit = (data: Schema): void => {
    setShowBottomSheet(false);
    updateSkillsAndBehaviors({ skillsSlugs: data.skills, behaviorsSlugs: data.behaviors });
  };

  const { formProps, control, submitButtonProps, setValue } = useKibbleForm({
    schema,
    defaultValues: {
      skills: selectedSkillsAndBehaviors.skillsSlugs,
      behaviors: selectedSkillsAndBehaviors.behaviorsSlugs,
    },
    onValidSubmit: handleBottomSheetSubmit,
  });

  const { field: skillsField } = useController({ control, name: 'skills' });

  const { field: behaviorsField } = useController({ control, name: 'behaviors' });

  const handleDesktopSkillsClick = (skills: string[]): void => {
    updateSkillsAndBehaviors({ ...selectedSkillsAndBehaviors, skillsSlugs: skills });
    skillsField.onChange(skills);
  };

  const handleDesktopBehaviorsClick = (behaviors: string[]): void => {
    updateSkillsAndBehaviors({ ...selectedSkillsAndBehaviors, behaviorsSlugs: behaviors });
    behaviorsField.onChange(behaviors);
  };

  const resetFields = (): void => {
    setValue('skills', []);
    setValue('behaviors', []);
    updateSkillsAndBehaviors({ skillsSlugs: [], behaviorsSlugs: [] });
  };

  return (
    <Form {...formProps} hideRequiredFieldWarning>
      <Flex display={['flex', 'none']}>
        <MobileTrainingNeedsInput
          selectedBehaviors={selectedSkillsAndBehaviors.behaviorsSlugs}
          selectedSkills={selectedSkillsAndBehaviors.skillsSlugs}
          behaviorsField={behaviorsField}
          skillsField={skillsField}
          submitButtonProps={submitButtonProps}
          showBottomSheet={showBottomSheet}
          setShowBottomSheet={setShowBottomSheet}
        />
      </Flex>
      <Flex display={['none', 'flex']} flexDirection="column">
        <DesktopTrainingNeedsInput
          skills={skillsField.value}
          behaviors={behaviorsField.value}
          handleDesktopBehaviorsClick={handleDesktopBehaviorsClick}
          handleDesktopSkillsClick={handleDesktopSkillsClick}
          resetFields={resetFields}
        />
      </Flex>
    </Form>
  );
};
